import { StoryblokComponent } from 'gatsby-source-storyblok';
import { Collapse } from 'react-collapse';
import React, { memo, useContext, useMemo } from 'react';
import { QuoteContext } from '../../../../contexts/Quote';
import { QuoteStageContext } from '../../../../contexts/QuoteStageContext';
import { Blok } from '../../../../interfaces/Blok.interface';
import { StoryblokMoveSpecialist } from '../../../../interfaces/StoryblokMoveSpecialist.interface';
import { StoryblokNode } from '../../../../interfaces/StoryblokNode.interface';
import { Theme } from '../../../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../../../BlokWrapper/BlokWrapper';
import { ContactInformationOpeningHoursRangeProps } from '../../../ContactBar/ContactInformation';
import SbImage from '../../../SbImage/SbImage';

interface QuoteMoveSpecialistBlokProps extends Blok {
  mobile_heading: string;
  desktop_heading: string;
}

interface QuoteMoveSpecialistProps {
  blok: QuoteMoveSpecialistBlokProps;
}

const QuoteMoveSpecialist: React.FC<QuoteMoveSpecialistProps> = ({ blok }) => {
  const { mobile_heading, desktop_heading } = blok;
  const { move_specialists } = useContext(QuoteStageContext);
  const { quoteDetails } = useContext(QuoteContext);
  const moveSpecialistId = quoteDetails?.moveSpecialistId;
  const [isMoveSpecialistOpen, setIsMoveSpecialistOpen] =
    React.useState<boolean>(false);

  const moveSpecialist: StoryblokNode<StoryblokMoveSpecialist> | undefined =
    useMemo(() => {
      return move_specialists.find(
        (moveSpecialist) => moveSpecialist.content.id === moveSpecialistId
      );
    }, [moveSpecialistId]);

  const DesktopHeading = (props: {
    heading: string;
    moveSpecialist: StoryblokNode<StoryblokMoveSpecialist>;
  }): JSX.Element => {
    const words: string[] = props.heading.split(' ');
    const full_name_placeholder: string = '<move-specialist>';
    return (
      <p className="font-dm-serif-display text-text/body text-4xl text-center leading-tight">
        {words.map((word: string, index: number) =>
          word === full_name_placeholder ? (
            <span
              key={word}
              className="font-dm-serif-display text-text/body-accent"
            >{`${moveSpecialist!.content.full_name}${
              word.includes('.') ? '.' : ''
            }${index < words.length - 1 ? ' ' : ''}`}</span>
          ) : (
            `${word}${index < words.length - 1 ? ' ' : ''}`
          )
        )}
      </p>
    );
  };

  const contactMethods = (
    <ul className="w-full px-8 pb-4 xl:px-0 xl:pb-0">
      {
        moveSpecialist?.content.available_contact_methods.reduce(
          (accumulator: JSX.Element[], availableContactMethod: Blok) => {
            // Get the current hour and day in the local timezone.
            const currentHour = new Date().getHours();
            const currentDay = new Date().getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

            // Define the start and end hours of the active period.
            const startHour = 9; // 9 AM
            const endHour = 16.8333; // 16:50 PM in decimal

            // Determine if it is currently within hours on a weekday.
            const isWeekend = currentDay === 0 || currentDay === 6;
            const isWithinBusinessHours =
              currentHour >= startHour && currentHour < endHour;

            // Show all options if it's within hours on a weekday.
            if (!isWeekend && isWithinBusinessHours) {
              accumulator.push(
                <StoryblokComponent
                  blok={availableContactMethod}
                  key={availableContactMethod._uid}
                />
              );
            }
            // If it's out of hours or weekend, only show Email and WhatsApp.
            else if (
              availableContactMethod.component === 'email' ||
              availableContactMethod.component === 'whatsapp'
            ) {
              accumulator.push(
                <StoryblokComponent
                  blok={availableContactMethod}
                  key={availableContactMethod._uid}
                />
              );
            }

            // Return the accumulator for the next iteration.
            return accumulator;
          },
          []
        ) // Start with an empty array as the initial accumulator value.
      }
    </ul>
  );

  return (
    <>
      {moveSpecialist && (
        <>
          {/*-- mobile and tablet --*/}
          <div className="flex items-center justify-between px-10 py-6 xl:hidden">
            <div className="flex items-center justify-start gap-x-4">
              <div className="w-16 h-16 rounded-full">
                <SbImage
                  asset={moveSpecialist.content.small_profile_image}
                  options={{ sizes: { base: 128 } }}
                />
              </div>
              <div>
                <p className="text-text/body text-base">{mobile_heading}</p>
                <p className="font-dm-serif-display text-2xl text-text/body-accent leading-none">
                  {moveSpecialist.content.full_name}
                </p>
              </div>
            </div>
            <button
              className="bg-button/primary-bg w-12 h-12 rounded-full flex items-center justify-center"
              onClick={() => setIsMoveSpecialistOpen(!isMoveSpecialistOpen)}
            >
              <span className="block xl:hidden h-[20px] w-[24px] bg-[url('/svgs/hamburger.svg')] bg-no-repeat bg-center overflow-hidden"></span>
            </button>
          </div>

          {/*-- desktop --*/}
          <div className="xl:flex xl:flex-col xl:items-center xl:justify-center xl:p-10 xl:gap-y-10">
            <div className="space-y-4 px-10 hidden xl:block">
              <DesktopHeading
                heading={desktop_heading}
                moveSpecialist={moveSpecialist}
              />

              {moveSpecialist.content.available_contact_hours && (
                <ul>
                  {moveSpecialist.content.available_contact_hours.map(
                    (
                      available_contact_hours_range: ContactInformationOpeningHoursRangeProps
                    ) => (
                      <li
                        key={available_contact_hours_range._uid}
                        className="flex items-center justify-center text-text/body"
                      >
                        <p>{available_contact_hours_range.day_label}</p>
                        <span className="px-1">-</span>
                        <p>{`${available_contact_hours_range.opening_time} to ${available_contact_hours_range.closing_time}`}</p>
                      </li>
                    )
                  )}
                </ul>
              )}
            </div>
            <div className="border-b border-b-border/light hidden xl:block">
              <div className="px-[72px]">
                <SbImage
                  asset={moveSpecialist.content.large_profile_image}
                  options={{ sizes: { base: 142 } }}
                />
              </div>
            </div>
            <div className="xl:hidden">
              <Collapse isOpened={isMoveSpecialistOpen}>
                {contactMethods}
              </Collapse>
            </div>
            <div className="w-full hidden xl:block">{contactMethods}</div>
          </div>
        </>
      )}
    </>
  );
};

export default memo(withBlokWrapper(QuoteMoveSpecialist, Theme.LIGHT));
